/**
 * VariationModal component for selecting a product variation.
 *
 * Displayed as a modal overlay when a variable product is clicked.
 * Lists all variations with their attributes, price, and stock status.
 *
 * @param {Object}   props
 * @param {Object}   props.product  - The parent variable product.
 * @param {Function} props.onSelect - Callback fired when a variation is selected; receives the variation object.
 * @param {Function} props.onClose  - Callback to close the modal.
 */
import { createElement, useEffect, useRef } from 'react';
import { formatPrice } from '../../utils/formatPrice';

export default function VariationModal({ product, onSelect, onClose }) {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  /**
   * Close on Escape key press.
   */
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /**
   * Trap focus inside the modal and auto-focus on mount.
   */
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  /**
   * Close when clicking the backdrop (not the modal content).
   */
  function handleBackdropClick(e) {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }

  const variations = product.variations || [];

  return (
    <div
      ref={overlayRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Select variation for ${product.name}`}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative max-h-[80vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="min-w-0 pr-4">
            <h2 className="truncate text-lg font-semibold text-gray-900">
              {product.name}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">Select a variation</p>
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className={
              'flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors ' +
              'hover:bg-gray-100 hover:text-gray-600 ' +
              'focus:outline-none focus:ring-2 focus:ring-pos-primary/30'
            }
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Variation list */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          {variations.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-400">
              No variations available for this product.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {variations.map((variation) => {
                const isOutOfStock = variation.stock_status === 'outofstock';

                /**
                 * Build a human-readable label from the variation's attributes.
                 * e.g. "Size: Large, Color: Red"
                 */
                const attributeLabel = Array.isArray(variation.attributes)
                  ? variation.attributes
                      .map((attr) => {
                        const name = attr.name || attr.option || '';
                        const option = attr.option || attr.value || '';
                        return name && option ? `${name}: ${option}` : option || name;
                      })
                      .filter(Boolean)
                      .join(' / ')
                  : variation.name || `#${variation.id}`;

                return (
                  <li key={variation.id}>
                    <button
                      type="button"
                      disabled={isOutOfStock}
                      onClick={() => onSelect(variation)}
                      className={
                        'flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors ' +
                        (isOutOfStock
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none')
                      }
                    >
                      {/* Variation details */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {attributeLabel}
                        </p>
                        {variation.sku && (
                          <p className="mt-0.5 text-xs text-gray-400">
                            SKU: {variation.sku}
                          </p>
                        )}
                      </div>

                      {/* Stock info */}
                      <div className="flex-shrink-0 text-right">
                        {isOutOfStock ? (
                          <span className="text-xs font-medium text-pos-danger">
                            Out of stock
                          </span>
                        ) : variation.stock_quantity !== null &&
                          variation.stock_quantity !== undefined ? (
                          <span
                            className={
                              'text-xs font-medium ' +
                              (variation.stock_quantity <= 5
                                ? 'text-pos-warning'
                                : 'text-pos-success')
                            }
                          >
                            {variation.stock_quantity} in stock
                          </span>
                        ) : (
                          <span className="text-xs text-pos-success">In stock</span>
                        )}
                      </div>

                      {/* Price */}
                      <div className="flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-900">
                          {formatPrice(parseFloat(variation.price) || 0)}
                        </span>
                      </div>

                      {/* Chevron */}
                      {!isOutOfStock && (
                        <svg
                          className="h-4 w-4 flex-shrink-0 text-gray-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
